import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTemplatesThunk,
  createTemplateThunk,
  updateTemplateThunk,
  cloneTemplateThunk,
  archiveTemplateThunk,
  validateTemplateThunk,
  unarchiveTemplateThunk,
  deleteTemplateThunk,
} from "../../../store/thunks/templateThunks";
import {
  setTemplateFilters,
  clearTemplateError,
  clearValidationResult,
} from "../../../store/slices/templateSlice";
import TemplateCard from "../components/TemplateCard";
import TemplateFormDrawer from "../components/TemplateFormDrawer";
import TemplatePreviewModal from "../components/TemplatePreviewModal";
import SearchBar from "../../../components/common/SearchBar";
import Filter from "../../../components/common/Filter";
import Button from "../../../components/common/Button";
import EmptyState from "../../../components/common/EmptyState";
import { notifySuccess, notifyError } from "../../../utils/notification";
import { PlusIcon, DocumentTextIcon } from "@heroicons/react/outline";

const TemplateManager = () => {
  const dispatch = useDispatch();
  const { list, loading, error, filters, validationResult } = useSelector(
    (state) => state.notificationTemplates
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({});

  // Safely handle undefined list - should be an array
  const templates = list || [];

  useEffect(() => {
    dispatch(fetchTemplatesThunk(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      notifyError(error);
      dispatch(clearTemplateError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (validationResult) {
      setPreviewData(validationResult.previews || {});
      setIsPreviewOpen(true);
      dispatch(clearValidationResult());
    }
  }, [validationResult, dispatch]);

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setIsDrawerOpen(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setIsDrawerOpen(true);
  };

  const handleCloneTemplate = async (template) => {
    const newKey = prompt(
      `Enter new template key (cloning from ${template.key}):`
    );
    if (newKey) {
      try {
        // Prepare the template data for cloning
        const templateData = {
          key: newKey.toUpperCase(), // Ensure uppercase for key consistency
          name: template.name
            ? `${template.name} (Clone)`
            : `${newKey} (Clone)`,
          titleTpl: template.titleTpl,
          bodyTpl: template.bodyTpl,
          imageUrlTpl: template.imageUrlTpl,
          iconUrlTpl: template.iconUrlTpl,
          deeplinkTpl: template.deeplinkTpl,
          actions: template.actions || [],
          isPromotional: template.isPromotional,
          channels: template.channels,
          category: template.category,
          priority: template.priority,
          tags: template.tags,
          description: template.description,
        };

        await dispatch(
          cloneTemplateThunk({
            key: template.key,
            templateData,
            locale: template.locale || "en-IN",
          })
        ).unwrap();
        notifySuccess("Template cloned successfully");
        dispatch(fetchTemplatesThunk(filters));
      } catch (error) {
        notifyError(error);
      }
    }
  };

  const handleArchiveTemplate = async (template, archive) => {
    const action = archive ? "archive" : "unarchive";
    if (window.confirm(`Are you sure you want to ${action} this template?`)) {
      try {
        if (archive) {
          await dispatch(
            archiveTemplateThunk({
              key: template.key,
              version: template.version,
            })
          ).unwrap();
        } else {
          await dispatch(
            unarchiveTemplateThunk({
              key: template.key,
              version: template.version,
            })
          ).unwrap();
        }

        notifySuccess(`Template ${action}d successfully`);
        dispatch(fetchTemplatesThunk(filters));
      } catch (error) {
        notifyError(error);
      }
    }
  };

  const handleDeleteTemplate = async (template, deleteAllVersions = false) => {
    const confirmMessage = deleteAllVersions
      ? `Are you sure you want to delete ALL versions of template "${template.key}"? This action cannot be undone.`
      : `Are you sure you want to delete version ${template.version} of template "${template.key}"? This action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        await dispatch(
          deleteTemplateThunk({
            key: template.key,
            version: deleteAllVersions ? null : template.version,
            locale: template.locale,
          })
        ).unwrap();

        notifySuccess(
          `Template ${
            deleteAllVersions ? "and all its versions " : ""
          }deleted successfully`
        );
        dispatch(fetchTemplatesThunk(filters));
      } catch (error) {
        // Check for active jobs conflict
        if (error && error.includes("active notification jobs")) {
          if (window.confirm("Template has active jobs. Force delete?")) {
            try {
              await dispatch(
                deleteTemplateThunk({
                  key: template.key,
                  version: deleteAllVersions ? null : template.version,
                  locale: template.locale,
                  force: true,
                })
              ).unwrap();

              notifySuccess(`Template force deleted successfully`);
              dispatch(fetchTemplatesThunk(filters));
            } catch (innerError) {
              notifyError(innerError);
            }
          }
        } else {
          notifyError(error);
        }
      }
    }
  };

  const handlePreviewTemplate = async (template) => {
    try {
      await dispatch(validateTemplateThunk(template)).unwrap();
    } catch (error) {
      notifyError(error);
    }
  };

  const handleSaveTemplate = async (templateData, options = {}) => {
    try {
      const isEditing = selectedTemplate !== null;
      const { bumpVersion = false } = options;

      if (isEditing) {
        // Update existing template
        await dispatch(
          updateTemplateThunk({
            key: selectedTemplate.key,
            templateData,
            bumpVersion,
          })
        ).unwrap();

        const action = bumpVersion ? "updated with new version" : "updated";
        notifySuccess(`Template ${action} successfully`);
      } else {
        // Create new template
        await dispatch(createTemplateThunk(templateData)).unwrap();
        notifySuccess("Template created successfully");
      }

      setIsDrawerOpen(false);
      setSelectedTemplate(null);
      dispatch(fetchTemplatesThunk(filters));
    } catch (error) {
      notifyError(error);
    }
  };

  const handleSaveAndBumpVersion = async (templateData) => {
    return handleSaveTemplate(templateData, { bumpVersion: true });
  };

  const handleFilterChange = (newFilters) => {
    dispatch(setTemplateFilters(newFilters));
  };

  const handleStatusFilter = (status) => {
    const statusMap = {
      All: undefined,
      Active: false,
      Archived: true,
    };
    handleFilterChange({ archived: statusMap[status] });
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      {/* Header - Responsive stacking on mobile */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 lg:mb-8">
        <div className="text-center sm:text-left">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
            Notification Templates
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            Create and manage reusable notification templates
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleCreateTemplate}
          className="flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-sm sm:text-base">Create Template</span>
        </Button>
      </div>

      {/* Filters - Improved responsive layout */}
      <div className="space-y-4 sm:space-y-0 sm:flex sm:flex-col lg:flex-row gap-4 mb-6 lg:mb-8">
        <div className="flex-1 min-w-0">
          <SearchBar
            placeholder="Search templates by name or key..."
            value={filters.search || ""}
            onChange={(search) => handleFilterChange({ search })}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <div className="w-full sm:w-auto">
            <Filter
              label="Status"
              options={["All", "Active", "Archived"]}
              defaultOption="Active"
              onSelect={handleStatusFilter}
            />
          </div>
          <div className="w-full sm:w-auto">
            <Filter
              label="Type"
              options={["All", "Promotional", "Transactional"]}
              defaultOption="All"
              onSelect={(type) =>
                handleFilterChange({
                  isPromotional:
                    type === "All" ? undefined : type === "Promotional",
                })
              }
            />
          </div>
        </div>
      </div>

      {/* Templates Grid - Updated for consistent card sizes */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-[450px] sm:h-[500px] bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
            />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="py-8 sm:py-12 lg:py-16">
          <EmptyState
            icon={DocumentTextIcon}
            title="No templates found"
            description="Create your first template to start sending notifications"
            action={
              <Button
                variant="primary"
                onClick={handleCreateTemplate}
                className="w-full sm:w-auto"
              >
                Create Template
              </Button>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
          {templates.map((template) => (
            <TemplateCard
              key={`${template.key}-${template.version}`}
              template={template}
              onEdit={handleEditTemplate}
              onClone={handleCloneTemplate}
              onArchive={handleArchiveTemplate}
              onUnarchive={handleArchiveTemplate}
              onPreview={handlePreviewTemplate}
              onDelete={handleDeleteTemplate}
            />
          ))}
        </div>
      )}

      {/* Template Form Drawer */}
      <TemplateFormDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        template={selectedTemplate}
        onSave={handleSaveTemplate}
        onSaveAndBumpVersion={handleSaveAndBumpVersion}
        isEditing={selectedTemplate !== null}
      />

      {/* Preview Modal */}
      <TemplatePreviewModal
        isOpen={isPreviewOpen}
        closeModal={() => setIsPreviewOpen(false)}
        previews={previewData}
      />
    </div>
  );
};

export default TemplateManager;
